import type { QueryClient } from '@tanstack/react-query';
import {
  AuditQuestionCompletionStatus,
  type GetAuditSummaryCommandOutput,
  type QuestionResponseDetails,
  type ResponseType,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import type { CacheSnapshot } from './patchAuditSummaryCache';
import {
  deriveParentCompletion,
  isSubEntryAnswered,
  type SubQuestionTypeRef,
} from './deriveParentResult';
import {
  EMPTY_RESPONSE,
  applyMetricsDiff,
  auditSummaryPredicate,
  buildNextCacheData,
} from './patchAuditCachePlumbing';

/**
 * Optimistic cache patch for an Enhanced Response Types (ERT)
 * data-collection VALUE save on a sub-question.
 *
 * Writes the target sub entry's `responseType` + `responseValue`
 * (stamping responder metadata), then re-derives the parent's
 * completion — a data-collection child counts as answered once it
 * holds a non-empty value — updating the parent `responseStatus`
 * and the `audit.metrics.completedQuestions` counter. The parent's
 * aggregate `responseResult` and the pass/fail/na buckets are NEVER
 * touched: data-collection subs are not scored, and only scored
 * children move the aggregate (see deriveParentResult).
 *
 * The other job this patch MUST do: land the saved value in the
 * summary cache so a subsequent save on a SIBLING sub-question
 * preserves it. Sibling saves rebuild `subResponses` from the cache
 * via `projectSubResponsesForPreservation`; if the cache didn't
 * reflect this value, that full-PATCH would silently drop it
 * server-side.
 *
 * Snapshots the whole summary output so rollback flows through the
 * shared `rollbackAuditQuestionResponseCache` on mutation error.
 */
export interface SubQuestionValuePatch {
  readonly subQuestionId: string;
  /** The data-collection response type echoed onto the cached entry. */
  readonly responseType: ResponseType;
  /** Captured value — empty string clears the cached value field. */
  readonly value: string;
  readonly respondedBy: string;
  /** Defaults to `new Date()` at patch time. */
  readonly respondedAt?: Date;
  /** Child id + responseType list from the full-question fetch — the
   *  summary cache only carries answered children, and parent
   *  completion needs each child's ERT type to know how it answers. */
  readonly subQuestionRefs: readonly SubQuestionTypeRef[];
}

export function snapshotAndPatchAuditSubQuestionValue(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: SubQuestionValuePatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeSubQuestionValuePatch(cachedData, questionId, patch, now);
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeSubQuestionValuePatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: SubQuestionValuePatch,
  now: Date,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMainResponse = targetQuestion.responseDetails?.mainQuestionResponse;
  const previousMainResult = previousMainResponse?.responseResult;
  const previousSubResponses = targetQuestion.responseDetails?.subQuestionResponses ?? [];
  const nextSubResponses = mergeSubValueForCache(previousSubResponses, patch, now);

  const previousCompleted = deriveParentCompletion(
    previousSubResponses,
    patch.subQuestionRefs,
  );
  const nextCompleted = deriveParentCompletion(nextSubResponses, patch.subQuestionRefs);
  const anySubAnswered = nextSubResponses.some((entry) =>
    isSubEntryAnswered(entry, entry.responseType),
  );

  // Status tracks structural completion; the parent's aggregate
  // RESULT is deliberately untouched — value saves are not scored.
  const nextMainResponse: QuestionResponseDetails = {
    ...(previousMainResponse ?? EMPTY_RESPONSE),
    responseStatus: deriveNextStatus(nextCompleted, anySubAnswered),
  };

  return buildNextCacheData(
    cachedData,
    targetIndex,
    nextMainResponse,
    nextSubResponses,
    applyMetricsDiff(
      cachedData.audit.metrics,
      previousMainResult,
      previousMainResult,
      previousCompleted,
      nextCompleted,
    ),
  );
}

/** COMPLETED > IN_PROGRESS > NOT_STARTED off the merged children. */
function deriveNextStatus(
  completed: boolean,
  anySubAnswered: boolean,
): AuditQuestionCompletionStatus {
  if (completed) return AuditQuestionCompletionStatus.COMPLETED;
  return anySubAnswered
    ? AuditQuestionCompletionStatus.IN_PROGRESS
    : AuditQuestionCompletionStatus.NOT_STARTED;
}

/** Replace or append the target sub entry with the new value + type. */
function mergeSubValueForCache(
  existing: readonly SubQuestionResponseEntry[],
  patch: SubQuestionValuePatch,
  now: Date,
): SubQuestionResponseEntry[] {
  const targetIndex = existing.findIndex(
    (entry) => entry.subQuestionId === patch.subQuestionId,
  );
  const previous = targetIndex >= 0 ? existing.at(targetIndex) : undefined;

  const nextResponse: QuestionResponseDetails = {
    ...(previous?.response ?? EMPTY_RESPONSE),
    respondedBy: patch.respondedBy,
    respondedAt: now,
  };

  const nextEntry: SubQuestionResponseEntry = {
    ...(previous ?? { subQuestionId: patch.subQuestionId }),
    subQuestionId: patch.subQuestionId,
    response: nextResponse,
    responseType: patch.responseType,
  };
  if (patch.value !== '') {
    nextEntry.responseValue = patch.value;
  } else {
    // Cleared value — drop the field so the cache mirrors the wire.
    delete nextEntry.responseValue;
  }

  if (targetIndex >= 0) {
    return existing.map((entry, i) => (i === targetIndex ? nextEntry : entry));
  }
  return [...existing, nextEntry];
}
